import type {ImageTileIndex, ImageTilesDescriptor} from "@cm/utils"

// Single shared source of truth for how a texture's requested tiles are laid out inside its per-texture
// atlas. Both platform-specific assembleTiles() implementations (browser + backend) and the tile-index-map
// builder must use the SAME assignment for a given requested-tile set, or the atlas image and the index map
// that's supposed to point into it would silently disagree about which slot holds which tile.

export type AtlasSlot = {slotX: number; slotY: number}

export type AtlasLayout = {
    cols: number
    rows: number
    // keyed by tileKey(tile) - see below
    slotByTileKey: Map<string, AtlasSlot>
}

// Canonical identity key for a requested tile - mirrors the makeKey convention already used elsewhere in
// this package (removeCoveredTiles/computeTileSetSignature in render-with-tiles.ts).
export function tileKey(tile: ImageTileIndex): string {
    return `${tile.mipLevel}:${tile.tileIndex.x}:${tile.tileIndex.y}`
}

// A tile's pixel payload is always tileWidth x tileHeight, regardless of which mip level it was requested
// at - so a single slot size (tile size + margin on every side) serves every mip level.
export function getAtlasSlotSize(descriptor: Pick<ImageTilesDescriptor, "tileWidth" | "tileHeight" | "tileMargin">): {width: number; height: number} {
    return {
        width: descriptor.tileWidth + 2 * descriptor.tileMargin,
        height: descriptor.tileHeight + 2 * descriptor.tileMargin,
    }
}

// Assigns each requested tile a grid slot in a simple near-square packing (no GPU max-texture-size/layer
// constraint applies here, unlike the three.js shared atlas - this is just a plain file per texture).
// Assignment order is coarse-to-fine (descending mip level) purely for deterministic/debuggable output;
// slots themselves never overlap so the order doesn't affect correctness.
export function assignAtlasSlots(requestedTiles: ImageTileIndex[]): AtlasLayout {
    const n = Math.max(1, requestedTiles.length) // never a zero-sized atlas, even for a degenerate empty request
    const cols = Math.ceil(Math.sqrt(n))
    const rows = Math.ceil(n / cols)

    const sorted = [...requestedTiles].sort((a, b) => b.mipLevel - a.mipLevel)
    const slotByTileKey = new Map<string, AtlasSlot>()
    sorted.forEach((tile, index) => {
        slotByTileKey.set(tileKey(tile), {slotX: index % cols, slotY: Math.floor(index / cols)})
    })

    return {cols, rows, slotByTileKey}
}
